import React, { useEffect, useState } from 'react';
import { useAdminAuthStore } from '../stores/authStore';
import { useDashboardStore } from '../stores/dashboardStore';
import toast from 'react-hot-toast';
import { FaTrash, FaPause, FaPlay, FaSearch } from 'react-icons/fa';
import { format } from 'date-fns';

export default function UsersPage() {
  const accessToken = useAdminAuthStore((state) => state.accessToken);
  const users = useDashboardStore((state) => state.users);
  const getUsers = useDashboardStore((state) => state.getUsers);
  const deleteUser = useDashboardStore((state) => state.deleteUser);
  const suspendUser = useDashboardStore((state) => state.suspendUser);
  const isLoading = useDashboardStore((state) => state.isLoading);

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (accessToken) {
      getUsers(accessToken, page);
    }
  }, [accessToken, page]);

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(accessToken, userId);
        toast.success('User deleted');
      } catch (error) {
        toast.error('Failed to delete user');
      }
    }
  };

  const handleSuspend = async (userId) => {
    try {
      await suspendUser(accessToken, userId);
      toast.success('User suspended');
    } catch (error) {
      toast.error('Failed to suspend user');
    }
  };

  const filteredUsers = users.filter((u) =>
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        <p className="text-gray-600 mt-1">Manage platform users</p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-3 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* Users Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="dashboard-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-4 font-semibold text-gray-700">Name</th>
                <th className="text-left p-4 font-semibold text-gray-700">Email</th>
                <th className="text-left p-4 font-semibold text-gray-700">Type</th>
                <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                <th className="text-left p-4 font-semibold text-gray-700">Joined</th>
                <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.user_id} className="table-row">
                    <td className="p-4">{user.full_name}</td>
                    <td className="p-4">{user.email}</td>
                    <td className="p-4">
                      <span className="badge-info">
                        {user.account_type === 'instructor' ? 'Instructor' : 'Student'}
                      </span>
                    </td>
                    <td className="p-4">
                      {user.is_suspended ? (
                        <span className="badge-danger">Suspended</span>
                      ) : (
                        <span className="badge-success">Active</span>
                      )}
                    </td>
                    <td className="p-4 text-sm">
                      {format(new Date(user.created_at), 'MMM dd, yyyy')}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        {!user.is_suspended ? (
                          <button
                            onClick={() => handleSuspend(user.user_id)}
                            className="btn-secondary btn-sm"
                            title="Suspend user"
                          >
                            <FaPause size={14} />
                          </button>
                        ) : (
                          <button
                            className="btn-secondary btn-sm opacity-50 cursor-not-allowed"
                            disabled
                            title="User suspended"
                          >
                            <FaPlay size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(user.user_id)}
                          className="btn-danger btn-sm"
                          title="Delete user"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
